import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AssignmentModal from "@/components/AssignmentModal";
import {
  Fire, MagnifyingGlass, ClipboardText, PaperPlaneTilt, UserPlus, Sparkle, FileText,
  CalendarBlank, ArrowsCounterClockwise, CaretLeft, CaretRight, CheckSquare, Square,
} from "@phosphor-icons/react";

/** Phase 6C — Hotlist as default candidate landing page.
 *  Server-side data: /api/hotlist returns enriched active candidates (owned rows for executives,
 *  full team for leaders). Filters + search + pagination are applied client-side over the fetched
 *  set (page size 25). Inline Assign/Reassign uses the shared AssignmentModal. Bulk auto-assign
 *  goes through /api/candidates/bulk-assign.
 */
const PRIORITY_CLS = {
  Hot: "bg-red-50 text-red-700 border-red-200",
  Urgent: "bg-amber-50 text-amber-700 border-amber-200",
  Normal: "bg-slate-50 text-slate-600 border-slate-200",
};
const PRIORITIES = ["Hot", "Urgent", "Normal"];
const STATUSES = ["Active", "Resumed", "On Hold"];
const PAGE_SIZE = 25;

export default function Hotlist() {
  const { user } = useAuth();
  const canAssign = ["admin", "team_lead", "hr", "sdl"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [placementFilter, setPlacementFilter] = useState("");
  const [techFilter, setTechFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");   // "", "assigned", "unassigned"
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [modalCand, setModalCand] = useState(null);
  const [view, setView] = useState("cards");                   // "cards" | "list"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hotlist");
      setRows(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load hotlist"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Distinct owners & placements for dropdowns
  const owners = useMemo(() => {
    const map = new Map();
    rows.forEach((c) => {
      if (c.assigned_employee_id && c.assigned_employee_name) {
        map.set(c.assigned_employee_id, c.assigned_employee_name);
      }
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const placements = useMemo(() =>
    [...new Set(rows.map((c) => c.placement_field).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const t = techFilter.trim().toLowerCase();
    return rows.filter((c) => {
      if (priority && c.priority !== priority) return false;
      if (status && c.service_status !== status) return false;
      if (ownerFilter && c.assigned_employee_id !== ownerFilter) return false;
      if (placementFilter && c.placement_field !== placementFilter) return false;
      if (assignedFilter === "assigned" && !c.assigned_employee_id) return false;
      if (assignedFilter === "unassigned" && c.assigned_employee_id) return false;
      if (t) {
        const techs = (c.technologies || []).join(" ").toLowerCase();
        if (!techs.includes(t)) return false;
      }
      if (q) {
        const hay = `${c.full_name || ""} ${c.email || ""} ${c.preferred_role || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, priority, status, ownerFilter, placementFilter, techFilter, assignedFilter]);

  useEffect(() => { setPage(1); setSelected(new Set()); }, [search, priority, status, ownerFilter, placementFilter, techFilter, assignedFilter]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(() =>
    filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = (checked) => setSelected(checked ? new Set(pageRows.map((c) => c.id)) : new Set());

  const bulkAutoAssign = async () => {
    if (!selected.size) return;
    if (!confirm(`Auto-assign ${selected.size} candidates using the recommendation engine?`)) return;
    try {
      const r = await api.post("/candidates/bulk-assign", {
        candidate_ids: [...selected], mode: "automatic",
        reason: "hotlist bulk auto-assign", reason_code: "business",
      });
      toast.success(`${r.data.ok?.length || 0} assigned, ${r.data.failed?.length || 0} failed`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
  };

  const resetFilters = () => {
    setSearch(""); setPriority(""); setStatus(""); setOwnerFilter("");
    setPlacementFilter(""); setTechFilter(""); setAssignedFilter("");
  };

  return (
    <div className="space-y-5" data-testid="hotlist-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Fire size={22} className="text-orange-500" /> Hotlist
          </h2>
          <p className="text-sm text-slate-500">
            {canAssign
              ? "Active candidates across your team. Assign, reassign, or filter — inline or in bulk."
              : "Your active candidates with quick access to documents and activity."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500" data-testid="hotlist-count">
            {filtered.length} of {rows.length}
          </div>
          {canAssign && (
            <button onClick={bulkAutoAssign} disabled={!selected.size}
                    data-testid="hotlist-bulk-auto-assign"
                    className="text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-md px-3 py-2 inline-flex items-center gap-1">
              <Sparkle size={14} /> Auto-assign ({selected.size})
            </button>
          )}
          <button onClick={() => setView(view === "cards" ? "list" : "cards")}
                  data-testid="hotlist-view-toggle"
                  className="text-sm border border-slate-200 rounded-md px-3 py-2">
            {view === "cards" ? "List view" : "Card view"}
          </button>
          <button onClick={load} title="Refresh"
                  data-testid="hotlist-refresh"
                  className="text-sm border border-slate-200 rounded-md px-3 py-2">
            <ArrowsCounterClockwise size={14} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search candidate name / email / role…"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="hotlist-search"
                 className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm" />
        </div>
        <select value={priority} onChange={(e) => setPriority(e.target.value)}
                data-testid="hotlist-priority-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="hotlist-status-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {canAssign && (
          <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
                  data-testid="hotlist-owner-filter"
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm">
            <option value="">All owners</option>
            {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        <select value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value)}
                data-testid="hotlist-placement-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All placement fields</option>
          {placements.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input placeholder="Technology…" value={techFilter} onChange={(e) => setTechFilter(e.target.value)}
               data-testid="hotlist-tech-filter"
               className="border border-slate-200 rounded-md px-3 py-2 text-sm w-40" />
        {canAssign && (
          <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}
                  data-testid="hotlist-assigned-filter"
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm">
            <option value="">All</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
          </select>
        )}
        <button onClick={resetFilters}
                data-testid="hotlist-reset-filters"
                className="text-xs text-slate-500 hover:text-slate-700 px-2">Reset</button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-400 py-10 text-center border border-slate-200 border-dashed rounded-md">
          No candidates match. Try clearing filters.
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pageRows.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3"
                 data-testid={`hotlist-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  {canAssign && (
                    <button onClick={() => toggle(c.id)} className="mt-0.5"
                            data-testid={`hotlist-select-${c.id}`}>
                      {selected.has(c.id) ? <CheckSquare size={16} className="text-sky-600" /> : <Square size={16} className="text-slate-300" />}
                    </button>
                  )}
                  <div className="min-w-0">
                    <Link to={`/candidates/${c.id}`} className="font-semibold text-slate-900 hover:text-sky-700 truncate block">
                      {c.full_name}
                    </Link>
                    <div className="text-xs text-slate-500">{c.preferred_role || "—"} · {c.visa_status || "no visa"}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      Owner: <b>{c.assigned_employee_name || <span className="text-amber-700">Unassigned</span>}</b>
                    </div>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_CLS[c.priority] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
                  {c.priority}
                </span>
              </div>

              <div className="flex flex-wrap gap-1 text-xs">
                {(c.technologies || []).slice(0, 4).map((t) => (
                  <span key={t} className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded-full">{t}</span>
                ))}
                {(c.technologies || []).length > 4 && <span className="text-slate-400">+{c.technologies.length - 4}</span>}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="flex flex-col items-center bg-slate-50 rounded-md py-2">
                  <span className="text-slate-500">Docs</span>
                  <span className="font-semibold text-slate-800">{c.document_count}</span>
                </div>
                <div className="flex flex-col items-center bg-slate-50 rounded-md py-2">
                  <span className="text-slate-500">Today apps</span>
                  <span className="font-semibold text-slate-800">{c.applications_today}</span>
                </div>
                <div className="flex flex-col items-center bg-slate-50 rounded-md py-2">
                  <span className="text-slate-500">Days left</span>
                  <span className={`font-semibold ${c.days_remaining <= 7 ? "text-red-600" : "text-slate-800"}`}>{c.days_remaining ?? "—"}</span>
                </div>
              </div>

              {c.last_evaluation && (
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <CalendarBlank size={12} /> Last eval: {c.last_evaluation} · {c.last_evaluation_status}
                </div>
              )}

              <div className="flex gap-2">
                <Link to={`/candidates/${c.id}`}
                      className="flex-1 text-xs bg-sky-600 hover:bg-sky-700 text-white rounded-md py-2 text-center inline-flex items-center justify-center gap-1">
                  <ClipboardText size={12} /> Open profile
                </Link>
                <Link to={`/daily-activity?candidate=${c.id}`}
                      data-testid={`hotlist-apply-${c.id}`}
                      className="flex-1 text-xs border border-slate-200 hover:bg-slate-50 rounded-md py-2 text-center inline-flex items-center justify-center gap-1">
                  <PaperPlaneTilt size={12} /> Log activity
                </Link>
                {canAssign && (
                  <button onClick={() => setModalCand(c)}
                          data-testid={`hotlist-assign-${c.id}`}
                          className="text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-md px-3 py-2 inline-flex items-center justify-center gap-1">
                    <UserPlus size={12} /> {c.assigned_employee_id ? "Reassign" : "Assign"}
                  </button>
                )}
              </div>
              {!c.has_resume && (
                <div className="text-xs text-red-600 inline-flex items-center gap-1">
                  <FileText size={12} /> Resume missing
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="hotlist-table">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                {canAssign && (
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" onChange={(e) => toggleAll(e.target.checked)} />
                  </th>
                )}
                <th className="text-left px-3 py-2">Candidate</th>
                <th className="text-left px-3 py-2">Placement</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Priority</th>
                <th className="text-left px-3 py-2">Days left</th>
                <th className="px-3 py-2 w-32" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hotlist-row-${c.id}`}>
                  {canAssign && (
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)}
                             data-testid={`hotlist-select-${c.id}`} />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <Link to={`/candidates/${c.id}`} className="font-medium hover:text-sky-700">{c.full_name}</Link>
                    <div className="text-xs text-slate-500">{c.preferred_role || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-500">{c.placement_field || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{c.assigned_employee_name || <span className="text-amber-700">Unassigned</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_CLS[c.priority] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{c.priority}</span>
                  </td>
                  <td className={`px-3 py-2 ${c.days_remaining <= 7 ? "text-red-600" : "text-slate-500"}`}>{c.days_remaining ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {canAssign && (
                      <button onClick={() => setModalCand(c)}
                              data-testid={`hotlist-assign-${c.id}`}
                              className="text-xs bg-slate-900 hover:bg-slate-800 text-white rounded-md px-2.5 py-1 inline-flex items-center gap-1">
                        <UserPlus size={12} /> {c.assigned_employee_id ? "Reassign" : "Assign"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </div>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="hotlist-page-prev"
                    className="px-2 py-1 border border-slate-200 rounded-md disabled:opacity-40">
              <CaretLeft size={14} />
            </button>
            <span className="px-2" data-testid="hotlist-page-info">{page} / {pages}</span>
            <button disabled={page === pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    data-testid="hotlist-page-next"
                    className="px-2 py-1 border border-slate-200 rounded-md disabled:opacity-40">
              <CaretRight size={14} />
            </button>
          </div>
        </div>
      )}

      {modalCand && (
        <AssignmentModal candidate={modalCand} onClose={() => setModalCand(null)} onDone={load} />
      )}
    </div>
  );
}
